import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import Select from "@/components/Select";
import { Controller, useForm } from "react-hook-form";
import useInteractionModes from "@/components/interactions/useInteractionModes";
import { InteractionInput } from "@/components/interactions/types";
import Switch from "@/components/Switch";
import { useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import AlgoliaJobsSearch from "@/components/JobsSearch/AlgoliaJobsSearch";
import { useCanManageJobPostsBool } from "@/hooks/useCapabilities";
import MultiSelect from "@/components/MultiSelect";
import { isEmpty, reject } from "lodash";
import type { InteractionCategoryMetadata } from "@/components/interactions/queries";
import {
  shouldSuggestPrivacy,
  selectionRequiresDefaultPrivateTopics,
  shouldUpdatePrivacyForSelection
} from "@/components/interactions/interactionCategoryPrivacy";

export default function EditMethodModal({
  open,
  setOpen,
  variables,
  setVariables,
  saveInteraction,
  interactionPrivate,
  disabled,
  interactionCategories,
  jobPostId
}: {
  open: boolean;
  setOpen: (value: boolean) => void;
  variables: InteractionInput;
  setVariables: Dispatch<SetStateAction<InteractionInput>>;
  saveInteraction: () => void;
  interactionPrivate: boolean;
  disabled: boolean;
  interactionCategories: InteractionCategoryMetadata[];
  jobPostId?: string;
}) {
  const { control, setValue } = useForm({
    mode: "onChange",
    defaultValues: {
      interactionDescription: variables.mode || "",
      interactionTopic: variables.categories || (variables.activityType ? [variables.activityType] : []),
      interactionPrivacy: interactionPrivate,
      jobPostId: jobPostId || ""
    }
  });

  const canManageJobPosts = useCanManageJobPostsBool();
  const { modes } = useInteractionModes(variables.mode);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    variables.categories || (variables.activityType ? [variables.activityType] : [])
  );

  // Stored tags that left the dropdown (renamed/removed in the config UI) are
  // shown as removable rows so the form never misrepresents its own state as
  // empty, and so saving without touching the picker preserves them.
  const topicOptions = useMemo(() => {
    const liveOptions = interactionCategories.map((category) => ({
      label: category.name,
      value: category.name,
      ...(category.isDefaultPrivate ? { description: "Private Topic" } : {})
    }));
    const liveValues = new Set(liveOptions.map((option) => option.value));
    const removedOptions = selectedCategories
      .filter((value) => value && !liveValues.has(value))
      .map((value) => ({ label: value, value, description: "Removed topic" }));
    return [...liveOptions, ...removedOptions];
  }, [interactionCategories, selectedCategories]);

  const [isPrivate, setIsPrivate] = useState<boolean>(interactionPrivate);
  const disableSave = disabled || selectedCategories.length == 0;

  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (open) {
      if (!wasOpenRef.current) {
        setValue("jobPostId", variables.jobPostId || "");
        const initialCategories = variables.categories || (variables.activityType ? [variables.activityType] : []);
        setSelectedCategories(initialCategories);
        setValue("interactionTopic", initialCategories);

        const initialPrivacy =
          interactionPrivate ?? shouldSuggestPrivacy(initialCategories, interactionCategories, variables.jobPostId);

        setIsPrivate(initialPrivacy);
        setValue("interactionPrivacy", initialPrivacy);
        setVariables((prev) => ({
          ...prev,
          privacy_restricted: initialPrivacy
        }));
      }
      wasOpenRef.current = true;
    } else {
      wasOpenRef.current = false;
    }
  }, [
    open,
    variables.categories,
    variables.activityType,
    variables.jobPostId,
    jobPostId,
    setValue,
    setVariables,
    interactionCategories,
    interactionPrivate
  ]);

  useEffect(() => {
    if (!open) {
      setIsPrivate(interactionPrivate);
    }
  }, [interactionPrivate, open]);

  return (
    <Dialog
      open={open}
      onClosed={() => {
        setVariables({
          ...variables,
          mode: null,
          activityType: null,
          categories: null,
          privacy_restricted: interactionPrivate
        });
        setOpen(false);
      }}
      headline="Edit Interaction"
      className="w-[490px] p-6 pr-2"
      actions={
        <>
          <Button
            variant="text"
            disabled={disabled}
            onClick={() => {
              setVariables({
                ...variables,
                mode: null,
                activityType: null,
                categories: null,
                privacy_restricted: interactionPrivate
              });
              setOpen(false);
            }}
          >
            Cancel
          </Button>

          <Button trailingIcon={false} icon="check" onClick={saveInteraction} disabled={disableSave}>
            Save
          </Button>
        </>
      }
      displayOverflow
    >
      <div className="flex flex-col gap-6">
        <Controller
          name="interactionDescription"
          control={control}
          rules={{ required: "Description is required" }}
          render={({ field }) => (
            <Select
              label="Method"
              required={true}
              options={modes}
              className="w-full"
              onSelect={(e) => {
                field.onChange(e);
                setVariables({ ...variables, mode: e });
              }}
              disabled={disabled}
              value={variables.mode || field.value}
              dropdownHeight="full"
            />
          )}
        />
        <Controller
          name="interactionTopic"
          control={control}
          rules={{ required: "Topic is required" }}
          render={({ fieldState }) => (
            <MultiSelect
              label="Topic"
              skipShowAll
              defaultLabel="Topics"
              options={topicOptions}
              selectedValues={selectedCategories}
              onSelect={(value, checked) => {
                const nextCategories = checked
                  ? [...selectedCategories, value]
                  : selectedCategories.filter((category) => category !== value);
                const cleanedCategories = reject(nextCategories, isEmpty);
                setSelectedCategories(cleanedCategories);

                // Only update privacy if we're not already private and the update wants to make it private
                const privacyUpdate = shouldUpdatePrivacyForSelection(
                  selectedCategories,
                  cleanedCategories,
                  interactionCategories
                );
                if (privacyUpdate !== null && !isPrivate) {
                  setIsPrivate(privacyUpdate);
                  setValue("interactionPrivacy", privacyUpdate);
                  setVariables((prev) => ({
                    ...prev,
                    activityType: null,
                    categories: cleanedCategories,
                    privacy_restricted: privacyUpdate
                  }));
                } else {
                  setVariables((prev) => ({
                    ...prev,
                    activityType: null,
                    categories: cleanedCategories
                  }));
                }
              }}
              onClear={() => {
                setSelectedCategories([]);
                // No privacy update on clear - privacy stays whatever user set it to
                setVariables((prev) => ({
                  ...prev,
                  activityType: null,
                  categories: []
                }));
              }}
              required={true}
              error={fieldState.invalid}
              errorText={fieldState.error?.message}
              disabled={disabled}
            />
          )}
        />

        {canManageJobPosts && (
          <Controller
            name="jobPostId"
            control={control}
            render={({ field }) => (
              <AlgoliaJobsSearch
                label="Job"
                placeholder="Search for jobs"
                disabled={disabled}
                jobPostId={field.value}
                onSelect={(jobId, _jobTitle) => {
                  field.onChange(jobId);
                  setVariables({
                    ...variables,
                    jobPostId: jobId,
                    privacy_restricted: true
                  });
                  setIsPrivate(true);
                  setValue("interactionPrivacy", true);
                }}
                onClear={() => {
                  field.onChange("");
                  const topicsForcePrivate = selectionRequiresDefaultPrivateTopics(
                    selectedCategories,
                    interactionCategories
                  );
                  setVariables({
                    ...variables,
                    jobPostId: "",
                    privacy_restricted: topicsForcePrivate
                  });
                  setIsPrivate(topicsForcePrivate);
                  setValue("interactionPrivacy", topicsForcePrivate);
                }}
              />
            )}
          />
        )}
        <Controller
          name="interactionPrivacy"
          control={control}
          render={({ field: { onChange } }) => {
            return (
              <Switch
                disabled={disabled}
                headline="Make Private"
                selected={isPrivate}
                supportingText="Only your department can view description"
                onChange={() => {
                  setIsPrivate(!isPrivate);
                  onChange(!isPrivate);
                  setVariables({ ...variables, privacy_restricted: !isPrivate });
                }}
              />
            );
          }}
        />
      </div>
    </Dialog>
  );
}
